import { useEffect, useRef } from 'react'
import Quill from 'quill'
import 'quill/dist/quill.snow.css'
import { INVITATION_FORMATS, parseDeltaValue } from '../lib/quillDelta'

export interface DeltaEditorProps {
  /**
   * The Delta document as a JSON string (the Quill canonical persistence
   * shape: `{"ops":[...]}`). Pass `null` for an empty editor.
   *
   * For backward compatibility with previously-stored plain-text values,
   * any string that fails to parse as a Quill Delta is treated as a single
   * `insert` op so legacy invitations continue to render.
   */
  value: string | null
  onChange: (next: string | null) => void
  placeholder?: string
  readOnly?: boolean
  ariaLabel?: string
  /**
   * Async handler invoked when the user clicks the toolbar's image button
   * and picks a file. Receives the chosen `File` and must return a
   * persisted URL — typically by uploading to Firebase Storage and
   * resolving to the download URL. The editor inserts an `<img>` at the
   * current cursor on resolve.
   *
   * If omitted, the image toolbar button is not rendered.
   */
  onUploadImage?: (file: File) => Promise<string>
}

interface DeltaShape {
  ops: ReadonlyArray<unknown>
}

/**
 * Hydrate the editor from a persisted value. Delegates the wire-shape parsing
 * (web wrapper `{ops}`, mobile bare `[{insert}]`, or legacy plain text) to the
 * shared [parseDeltaValue] so the editor and the read-only DeltaRenderer never
 * diverge on which shapes they accept; a plain-text value becomes a single
 * `insert` op so legacy invitations still render.
 */
function parseDelta(value: string | null): DeltaShape {
  const parsed = parseDeltaValue(value)
  switch (parsed.kind) {
    case 'delta':
      return { ops: parsed.ops }
    case 'plain':
      return { ops: [{ insert: parsed.text }] }
    case 'empty':
      return { ops: [] }
  }
}

/**
 * A controlled Quill editor that round-trips its content as a JSON-serialized
 * Delta document. Use it where you previously had a `<textarea>` for rich
 * text — the editor's value is a single nullable string, not a structured
 * Delta object, so storage/transport stays string-shaped.
 *
 * Toolbar is intentionally minimal (bold, italic, lists, links). Extend by
 * passing extra modules via Quill's API in a future revision if more
 * formatting is needed.
 */
export function DeltaEditor({
  value,
  onChange,
  placeholder,
  readOnly = false,
  ariaLabel,
  onUploadImage,
}: DeltaEditorProps) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const quillRef = useRef<Quill | null>(null)
  const lastEmittedRef = useRef<string | null>(value)
  // Captured once at editor mount via closure (see useEffect deps below). The
  // ref pattern keeps the toolbar handler stable while letting the parent swap
  // in a new uploader (e.g. when a draft ID becomes available) without
  // re-mounting Quill.
  const onUploadImageRef = useRef(onUploadImage)
  useEffect(() => {
    onUploadImageRef.current = onUploadImage
  }, [onUploadImage])

  useEffect(() => {
    if (!containerRef.current || quillRef.current) return

    const toolbarRows: Array<Array<string | Record<string, string>>> = [
      ['bold', 'italic', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link'],
    ]
    if (onUploadImageRef.current) {
      toolbarRows.push(['image'])
    }
    toolbarRows.push(['clean'])

    const quill = new Quill(containerRef.current, {
      theme: 'snow',
      readOnly,
      placeholder,
      formats: [...INVITATION_FORMATS],
      modules: {
        toolbar: {
          container: toolbarRows,
          handlers: {
            image: () => {
              const handler = onUploadImageRef.current
              if (!handler) return
              const input = document.createElement('input')
              input.type = 'file'
              input.accept = 'image/*'
              input.onchange = async () => {
                const file = input.files?.[0]
                if (!file) return
                try {
                  const url = await handler(file)
                  const range = quill.getSelection(true)
                  quill.insertEmbed(range?.index ?? quill.getLength(), 'image', url, 'user')
                  quill.setSelection((range?.index ?? quill.getLength()) + 1, 0, 'user')
                } catch (err) {
                  // Surface in the console so the user can see why the upload
                  // failed; the editor state is unchanged.
                  // eslint-disable-next-line no-console
                  console.error('DeltaEditor: image upload failed', err)
                }
              }
              input.click()
            },
          },
        },
      },
    })

    const initial = parseDelta(value)
    quill.setContents(initial as never, 'silent')

    quill.on('text-change', () => {
      const ops = quill.getContents().ops
      const isEmpty =
        ops.length === 0 ||
        (ops.length === 1 && (ops[0] as { insert?: unknown }).insert === '\n')
      const serialized = isEmpty ? null : JSON.stringify({ ops })
      lastEmittedRef.current = serialized
      onChange(serialized)
    })

    quillRef.current = quill
    // We deliberately initialise once. `onChange` is captured per render via
    // closure on Quill's `text-change` handler — re-binding it on every parent
    // render would tear down + recreate the editor and lose cursor state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Reflect external `value` changes (e.g. when a draft hydrates from the
  // server) without re-creating Quill. Only update when the incoming value
  // differs from what the editor last emitted, so user typing isn't fought.
  useEffect(() => {
    const quill = quillRef.current
    if (!quill) return
    if (value === lastEmittedRef.current) return

    const incoming = parseDelta(value)
    quill.setContents(incoming as never, 'silent')
    lastEmittedRef.current = value
  }, [value])

  useEffect(() => {
    quillRef.current?.enable(!readOnly)
  }, [readOnly])

  return (
    <div
      ref={containerRef}
      aria-label={ariaLabel}
      data-testid="delta-editor"
      style={{ minHeight: 160 }}
    />
  )
}
